import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { fetchEpicPlan } from '../../../../lib/api/epics';
import type { EpicDetail } from '../../../../../../shared/types/epics';
import { ScopeCard } from '../plan/ScopeCard';
import { StoryBreakdownTable } from '../plan/StoryBreakdownTable';
import { PlanEmptyState, isParityEpic } from '../plan/PlanEmptyState';

/** True when ScopeCard / StoryBreakdownTable would render something. */
export function hasPlanContent(epic: EpicDetail): boolean {
  const scope = epic.model === 'cross-repo' && (epic.involvedRepos ?? []).length > 0;
  const stories = epic.kind !== 'bugfix' && epic.stories.some((s) => s.storyPoints !== null || s.lifetimeCostUsd > 0);
  return scope || stories;
}

/** The epic's own overview/requirements, from the plan — "what is this about". */
const EpicOverview: React.FC<{ epic: EpicDetail }> = ({ epic }) => {
  const q = useQuery({
    queryKey: ['epic-plan', String(epic.id)],
    queryFn: () => fetchEpicPlan(String(epic.id)),
    retry: false,
    staleTime: 60_000,
  });
  const body = q.data?.epicBody?.trim();
  if (!body) return null;
  return (
    <section data-testid="epic-overview" className="rounded-xl border border-slate-200 bg-white px-4 py-3">
      <h2 className="text-sm font-semibold text-slate-800 mb-1">Overview</h2>
      <details>
        <summary className="cursor-pointer text-xs text-primary-600">What this epic is about</summary>
        <p className="mt-2 text-sm text-slate-700 whitespace-pre-wrap">{body}</p>
      </details>
    </section>
  );
};

export const PlanTab: React.FC<{ epic: EpicDetail }> = ({ epic }) => (
  <div className="space-y-5">
    <EpicOverview epic={epic} />
    {/* FORGE-348: parity epics have no story plan — explain where the work is. */}
    {(isParityEpic(epic) || !hasPlanContent(epic)) && <PlanEmptyState epic={epic} />}
    <ScopeCard epic={epic} />
    <StoryBreakdownTable epic={epic} />
  </div>
);
